import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Modal,
  Alert,
  Animated,
  Dimensions,
  ActivityIndicator,
  TextInput,
  Image,
  RefreshControl,
  Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// Action Types Configuration
const ACTION_TYPES = {
  ASSESSMENT: 'assessment',
  INTERVIEW: 'interview',
  DOCUMENT_UPLOAD: 'document_upload',
  SKILL_TEST: 'skill_test',
  VIDEO_INTERVIEW: 'video_interview',
  BACKGROUND_CHECK: 'background_check',
  REFERENCE_CHECK: 'reference_check',
  OFFER_NEGOTIATION: 'offer_negotiation',
  CONTRACT_SIGNING: 'contract_signing',
  ONBOARDING: 'onboarding',
};

// Action Screen Configuration
const ACTION_SCREEN_CONFIG = {
  [ACTION_TYPES.ASSESSMENT]: {
    title: 'Technical Assessment',
    subtitle: 'Complete your technical evaluation',
    icon: 'school',
    color: '#3498db',
    gradient: ['#3498db', '#2980b9'],
    description: 'This assessment will evaluate your technical skills and problem-solving abilities.',
    estimatedTime: '45-60 minutes',
    sections: ['Programming Logic', 'Problem Solving', 'Code Review', 'System Design'],
  },
  [ACTION_TYPES.INTERVIEW]: {
    title: 'Video Interview',
    subtitle: 'Join your scheduled interview',
    icon: 'videocam',
    color: '#e74c3c',
    gradient: ['#e74c3c', '#c0392b'],
    description: 'Connect with our hiring team for a comprehensive interview session.',
    estimatedTime: '30-45 minutes',
    sections: ['Introduction', 'Technical Discussion', 'Cultural Fit', 'Q&A Session'],
  },
  [ACTION_TYPES.DOCUMENT_UPLOAD]: {
    title: 'Document Upload',
    subtitle: 'Submit required documents',
    icon: 'document-text',
    color: '#f39c12',
    gradient: ['#f39c12', '#e67e22'],
    description: 'Please upload all necessary documents for verification.',
    estimatedTime: '10-15 minutes',
    sections: ['Identity Verification', 'Educational Certificates', 'Work Experience', 'References'],
  },
  [ACTION_TYPES.SKILL_TEST]: {
    title: 'Skill Assessment',
    subtitle: 'Demonstrate your expertise',
    icon: 'code-slash',
    color: '#9b59b6',
    gradient: ['#9b59b6', '#8e44ad'],
    description: 'Practical assessment to showcase your technical abilities.',
    estimatedTime: '60-90 minutes',
    sections: ['Coding Challenge', 'Algorithm Design', 'Code Optimization', 'Testing'],
  },
  [ACTION_TYPES.VIDEO_INTERVIEW]: {
    title: 'Live Interview',
    subtitle: 'Real-time video conversation',
    icon: 'people',
    color: '#1abc9c',
    gradient: ['#1abc9c', '#16a085'],
    description: 'Interactive interview session with our technical team.',
    estimatedTime: '45-60 minutes',
    sections: ['Technical Deep Dive', 'Project Discussion', 'Problem Solving', 'Team Fit'],
  },
  [ACTION_TYPES.BACKGROUND_CHECK]: {
    title: 'Background Verification',
    subtitle: 'Complete background check process',
    icon: 'shield-checkmark',
    color: '#34495e',
    gradient: ['#34495e', '#2c3e50'],
    description: 'Verify your background information and references.',
    estimatedTime: '5-10 minutes',
    sections: ['Personal Information', 'Employment History', 'Education Verification', 'References'],
  },
  [ACTION_TYPES.REFERENCE_CHECK]: {
    title: 'Reference Verification',
    subtitle: 'Provide professional references',
    icon: 'people-circle',
    color: '#27ae60',
    gradient: ['#27ae60', '#229954'],
    description: 'Submit contact information for your professional references.',
    estimatedTime: '10-15 minutes',
    sections: ['Previous Managers', 'Colleagues', 'Clients', 'Academic References'],
  },
  [ACTION_TYPES.OFFER_NEGOTIATION]: {
    title: 'Offer Discussion',
    subtitle: 'Review and negotiate your offer',
    icon: 'chatbubbles',
    color: '#e67e22',
    gradient: ['#e67e22', '#d35400'],
    description: 'Discuss the details of your job offer and terms.',
    estimatedTime: '20-30 minutes',
    sections: ['Salary Discussion', 'Benefits Review', 'Start Date', 'Terms & Conditions'],
  },
  [ACTION_TYPES.CONTRACT_SIGNING]: {
    title: 'Contract Signing',
    subtitle: 'Finalize your employment contract',
    icon: 'document-text',
    color: '#2ecc71',
    gradient: ['#2ecc71', '#27ae60'],
    description: 'Review and sign your employment contract.',
    estimatedTime: '15-20 minutes',
    sections: ['Contract Review', 'Digital Signature', 'Terms Acceptance', 'Final Confirmation'],
  },
  [ACTION_TYPES.ONBOARDING]: {
    title: 'Onboarding Process',
    subtitle: 'Welcome to the team!',
    icon: 'rocket',
    color: '#8e44ad',
    gradient: ['#8e44ad', '#7d3c98'],
    description: 'Complete your onboarding and get ready to start.',
    estimatedTime: '30-45 minutes',
    sections: ['Company Introduction', 'Team Setup', 'Tools & Access', 'First Day Preparation'],
  },
};

const ActionScreen = ({ route, navigation }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { actionType, applicationData, jobData } = route.params || {};
  
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [actionProgress, setActionProgress] = useState(0);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [actionData, setActionData] = useState({});
  const [showInstructions, setShowInstructions] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notes, setNotes] = useState('');
  const [feedback, setFeedback] = useState('');
  
  const progressAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;
  
  const config = ACTION_SCREEN_CONFIG[actionType] || ACTION_SCREEN_CONFIG[ACTION_TYPES.ASSESSMENT];

  useEffect(() => {
    initializeAction();
    animateEntrance();
  }, []);

  const initializeAction = async () => {
    try {
      setLoading(true);
      
      // Load saved progress if exists
      const savedProgress = await AsyncStorage.getItem(`action_${actionType}_${applicationData?.id}`);
      if (savedProgress) {
        const progress = JSON.parse(savedProgress);
        setCurrentStep(progress.currentStep || 0);
        setActionProgress(progress.progress || 0);
        setActionData(progress.data || {});
        setActionCompleted(progress.completed || false);
      }
      
      // Simulate loading time
      setTimeout(() => {
        setLoading(false);
      }, 1500);
    } catch (error) {
      console.error('Error initializing action:', error);
      setLoading(false);
    }
  };

  const animateEntrance = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 50,
        friction: 7,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const updateProgress = (step) => {
    const progress = ((step + 1) / config.sections.length) * 100;
    setActionProgress(progress);
    
    Animated.timing(progressAnim, {
      toValue: progress,
      duration: 500,
      useNativeDriver: false,
    }).start();
  };

  const handleStepComplete = async (stepIndex) => {
    const newStep = stepIndex + 1;
    setCurrentStep(newStep);
    updateProgress(stepIndex);
    
    // Save progress
    const progressData = {
      currentStep: newStep,
      progress: ((stepIndex + 1) / config.sections.length) * 100,
      data: actionData,
      completed: newStep >= config.sections.length,
      timestamp: new Date().toISOString(),
    };
    
    await AsyncStorage.setItem(
      `action_${actionType}_${applicationData?.id}`,
      JSON.stringify(progressData)
    );
    
    if (newStep >= config.sections.length) {
      setActionCompleted(true);
      showCompletionAlert();
    }
  };

  const showCompletionAlert = () => {
    Alert.alert(
      '🎉 Action Completed!',
      `You have successfully completed the ${config.title}. Your progress has been saved and the hiring team will be notified.`,
      [
        {
          text: 'Continue',
          onPress: () => navigation.goBack(),
        },
      ]
    );
  };

  const handleStartAction = () => {
    setShowInstructions(false);
    if (currentStep === 0 && !actionCompleted) {
      handleStepComplete(-1); // Start from step 0
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await initializeAction();
    setRefreshing(false);
  };

  const renderHeader = () => (
    <LinearGradient colors={config.gradient} style={styles.header}>
      <TouchableOpacity 
        style={styles.backButton} 
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="arrow-back" size={24} color="#fff" />
      </TouchableOpacity>
      
      <View style={styles.headerContent}>
        <View style={styles.iconContainer}>
          <Ionicons name={config.icon} size={32} color="#fff" />
        </View>
        <Text style={styles.headerTitle}>{config.title}</Text>
        <Text style={styles.headerSubtitle}>{config.subtitle}</Text>
      </View>
      
      <View style={styles.progressContainer}>
        <Text style={styles.progressText}>
          {Math.round(actionProgress)}% Complete
        </Text>
        <View style={styles.progressBar}>
          <Animated.View 
            style={[
              styles.progressFill,
              {
                width: progressAnim.interpolate({
                  inputRange: [0, 100],
                  outputRange: ['0%', '100%'],
                }),
              },
            ]}
          />
        </View>
      </View>
    </LinearGradient>
  );

  const renderInstructions = () => (
    <View style={styles.instructionsContainer}>
      <View style={styles.instructionsHeader}>
        <MaterialCommunityIcons name="information" size={24} color={config.color} />
        <Text style={styles.instructionsTitle}>Instructions</Text>
      </View>
      
      <Text style={styles.description}>{config.description}</Text>
      
      <View style={styles.timeContainer}>
        <Ionicons name="time" size={20} color="#666" />
        <Text style={styles.timeText}>Estimated time: {config.estimatedTime}</Text>
      </View>
      
      <View style={styles.sectionsContainer}>
        <Text style={styles.sectionsTitle}>What to expect:</Text>
        {config.sections.map((section, index) => (
          <View key={index} style={styles.sectionItem}>
            <View style={styles.sectionNumber}>
              <Text style={styles.sectionNumberText}>{index + 1}</Text>
            </View>
            <Text style={styles.sectionText}>{section}</Text>
          </View>
        ))}
      </View>
      
      <TouchableOpacity 
        style={[styles.startButton, { backgroundColor: config.color }]}
        onPress={handleStartAction}
      >
        <Text style={styles.startButtonText}>
          {actionCompleted ? 'Review Completed Action' : 'Start Action'}
        </Text>
        <Ionicons name="arrow-forward" size={20} color="#fff" />
      </TouchableOpacity>
    </View>
  );

  const renderActionContent = () => (
    <View style={styles.actionContainer}>
      <View style={styles.stepsContainer}>
        {config.sections.map((section, index) => (
          <View key={index} style={styles.stepCard}>
            <View style={styles.stepHeader}>
              <View style={[
                styles.stepIndicator,
                {
                  backgroundColor: index < currentStep ? config.color : 
                                  index === currentStep ? config.color : '#e0e0e0',
                }
              ]}>
                {index < currentStep ? (
                  <Ionicons name="checkmark" size={16} color="#fff" />
                ) : (
                  <Text style={[
                    styles.stepNumber,
                    { color: index === currentStep ? '#fff' : '#666' }
                  ]}>
                    {index + 1}
                  </Text>
                )}
              </View>
              <Text style={[
                styles.stepTitle,
                { color: index <= currentStep ? '#333' : '#999' }
              ]}>
                {section}
              </Text>
            </View>
            
            {index === currentStep && !actionCompleted && (
              <View style={styles.stepActions}>
                <TextInput
                  style={styles.notesInput}
                  placeholder="Add notes for this step..."
                  value={notes}
                  onChangeText={setNotes}
                  multiline
                />
                <TouchableOpacity
                  style={[styles.completeStepButton, { backgroundColor: config.color }]}
                  onPress={() => handleStepComplete(index)}
                >
                  <Text style={styles.completeStepText}>Complete Step</Text>
                </TouchableOpacity>
              </View>
            )}
            
            {index < currentStep && (
              <View style={styles.completedStep}>
                <Ionicons name="checkmark-circle" size={20} color={config.color} />
                <Text style={styles.completedText}>Completed</Text>
              </View>
            )}
          </View>
        ))}
      </View>
      
      {actionCompleted && (
        <View style={styles.completionContainer}>
          <LinearGradient 
            colors={['#2ecc71', '#27ae60']} 
            style={styles.completionCard}
          >
            <Ionicons name="checkmark-circle" size={48} color="#fff" />
            <Text style={styles.completionTitle}>Action Completed!</Text>
            <Text style={styles.completionText}>
              Great job! You've successfully completed all steps.
            </Text>
            
            <TextInput
              style={styles.feedbackInput}
              placeholder="Share your feedback about this process..."
              value={feedback}
              onChangeText={setFeedback}
              multiline
              placeholderTextColor="#rgba(255,255,255,0.7)"
            />
            
            <TouchableOpacity 
              style={styles.submitFeedbackButton}
              onPress={() => {
                Alert.alert('Thank you!', 'Your feedback has been submitted.');
                navigation.goBack();
              }}
            >
              <Text style={styles.submitFeedbackText}>Submit Feedback</Text>
            </TouchableOpacity>
          </LinearGradient>
        </View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color={config.color} />
        <Text style={styles.loadingText}>Preparing your action...</Text>
      </View>
    );
  }

  return (
    <Animated.View 
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      {renderHeader()}
      
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
        }
      >
        {showInstructions ? renderInstructions() : renderActionContent()}
      </ScrollView>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 50 : 30,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  backButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 30,
    left: 20,
    zIndex: 1,
    padding: 8,
  },
  headerContent: {
    alignItems: 'center',
    marginTop: 20,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    textAlign: 'center',
    marginTop: 4,
  },
  progressContainer: {
    marginTop: 20,
  },
  progressText: {
    fontSize: 14,
    color: '#fff',
    textAlign: 'center',
    marginBottom: 8,
  },
  progressBar: {
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.3)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#fff',
    borderRadius: 2,
  },
  content: {
    flex: 1,
  },
  instructionsContainer: {
    padding: 20,
  },
  instructionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  instructionsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginLeft: 8,
  },
  description: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    marginBottom: 20,
  },
  timeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    padding: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  timeText: {
    fontSize: 14,
    color: '#666',
    marginLeft: 8,
  },
  sectionsContainer: {
    marginBottom: 30,
  },
  sectionsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  sectionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    padding: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  sectionNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#e3f2fd',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  sectionNumberText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1976d2',
  },
  sectionText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  startButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  startButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginRight: 8,
  },
  actionContainer: {
    padding: 20,
  },
  stepsContainer: {
    marginBottom: 20,
  },
  stepCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  stepIndicator: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  stepActions: {
    marginTop: 12,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 12,
  },
  completeStepButton: {
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  completeStepText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  completedStep: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  completedText: {
    fontSize: 14,
    color: '#27ae60',
    marginLeft: 8,
    fontWeight: '500',
  },
  completionContainer: {
    marginTop: 20,
  },
  completionCard: {
    padding: 24,
    borderRadius: 16,
    alignItems: 'center',
  },
  completionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    marginTop: 16,
    marginBottom: 8,
  },
  completionText: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    textAlign: 'center',
    marginBottom: 20,
  },
  feedbackInput: {
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#fff',
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  submitFeedbackButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    width: '100%',
  },
  submitFeedbackText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default ActionScreen;
